import { useState, useRef, useMemo } from 'react'
import toast from 'react-hot-toast';
import { PageLoader } from '../../components/common/Loader';
import { useParams, useNavigate } from 'react-router-dom'
import Breadcrumb from '../../components/common/Breadcrumb'
import { useAuth } from '../../contexts/AuthContext'
import {
    IconCheck, IconBan, IconChevronLeft, IconClock, IconBeach, IconCalendarMonth, IconSignature
} from '@tabler/icons-react'
import {
    useTimesheet,
    useHrApproveTimesheet,
    useRejectTimesheet,
    useMyApprovedLeaves
} from '../../hooks/useTimesheets';
import { useGoogleHolidays } from '../../hooks/useGoogleHolidays';
import { months } from '../../data/timesheetData';

export default function HrTimesheetApprovalDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const orgId = user?.orgId;

    const { data: timesheetData, isLoading: isTimesheetLoading } = useTimesheet(id, orgId);

    const subordinateEmployeeId = timesheetData?.employee?.id || timesheetData?.employee;
    const tsYear = timesheetData?.year;
    const tsMonth = timesheetData?.month;

    const { data: googleHolidaysData } = useGoogleHolidays(tsYear);
    const { data: approvedLeavesData } = useMyApprovedLeaves(orgId, null, null, subordinateEmployeeId);

    const approveTimesheet = useHrApproveTimesheet();
    const rejectTimesheet = useRejectTimesheet();

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const leftPaneRef = useRef(null);
    const rightPaneRef = useRef(null);

    const formatDateLocal = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const isWeekend = (date) => { const d = date.getDay(); return d === 0 || d === 6; };

    // Build holiday lookup from Nager.Date data
    const holidayLookup = useMemo(() => {
        const lookup = {};
        (googleHolidaysData || []).forEach(h => {
            if (h.date) lookup[h.date] = { name: h.name || 'Holiday', hours: 8 };
        });
        return lookup;
    }, [googleHolidaysData]);

    const leaveLookup = useMemo(() => {
        const lookup = {};
        if (!approvedLeavesData) return lookup;
        (approvedLeavesData.leave_dates || []).forEach(dateStr => {
            if (!dateStr) return;
            const d = new Date(dateStr).getDay();
            if (d !== 0 && d !== 6) lookup[dateStr] = { hours: 8 };
        });
        return lookup;
    }, [approvedLeavesData]);

    const employeeName = timesheetData?.employee_name || timesheetData?.employee?.name || 'Employee';
    const timesheetStatus = timesheetData?.status || 'manager_approved';
    const signedAt = timesheetData?.signed_at || timesheetData?.submitted_at;
    const managerApprovedAt = timesheetData?.manager_approved_at;
    const daysInMonth = tsYear && tsMonth ? new Date(tsYear, tsMonth, 0).getDate() : 0;
    const periodDays = Array.from({ length: daysInMonth }, (_, i) => new Date(tsYear, tsMonth - 1, i + 1));

    const timeEntries = useMemo(() => {
        return (timesheetData?.lines || []).map(line => ({
            id: line.id,
            description: line.description || '',
            project: line.project?.name || 'No Project',
            projectCode: line.project?.id || '',
            plc: line.activity_code || '',
            payType: line.pay_type || 'Regular',
            isLeave: line.is_leave || false,
            isTimeOff: line.is_time_off || false,
            hours: line.entries ? Object.entries(line.entries).reduce((acc, [dateStr, val]) => {
                const d = parseInt(dateStr.split('-')[2], 10);
                acc[d - 1] = typeof val === 'object' ? parseFloat(val.hours) : parseFloat(val);
                return acc;
            }, {}) : {}
        }));
    }, [timesheetData?.lines]);

    const calculateHolidayHours = () =>
        periodDays.reduce((sum, date) => sum + (holidayLookup[formatDateLocal(date)]?.hours || 0), 0);

    const calculateLeaveHours = () =>
        periodDays.reduce((sum, date) => {
            if (isWeekend(date)) return sum;
            return sum + (leaveLookup[formatDateLocal(date)]?.hours || 0);
        }, 0);

    const calculateProjectHours = () => {
        let total = 0;
        timeEntries.forEach(entry => {
            periodDays.forEach((date, dayIdx) => {
                const ds = formatDateLocal(date);
                if (!leaveLookup[ds] && !isWeekend(date) && entry.hours[dayIdx]) {
                    total += entry.hours[dayIdx];
                }
            });
        });
        return total;
    };

    const calculateRowTotalExcludingLeave = (entryHours) => {
        let total = 0;
        periodDays.forEach((date, dayIdx) => {
            const ds = formatDateLocal(date);
            if (!leaveLookup[ds] && !isWeekend(date) && entryHours[dayIdx]) {
                total += entryHours[dayIdx];
            }
        });
        return total;
    };

    const calculateLeaveHoursForFirstRow = () =>
        periodDays.reduce((sum, date) => {
            if (isWeekend(date)) return sum;
            return sum + (leaveLookup[formatDateLocal(date)]?.hours || 0);
        }, 0);

    const calculateGrandTotal = () => calculateProjectHours() + calculateHolidayHours();

    const handleConfirmApprove = () => {
        approveTimesheet.mutate({ id, orgId }, {
            onSuccess: () => { setShowApproveModal(false); navigate('/approvals/timesheet/hr'); }
        });
    };

    const handleConfirmReject = () => {
        if (!rejectReason.trim()) return toast.error('Reason required');
        rejectTimesheet.mutate({ id, reason: rejectReason, orgId }, {
            onSuccess: () => { setShowRejectModal(false); navigate('/approvals/timesheet/hr'); }
        });
    };

    const handleVerticalScroll = (e) => {
        const { scrollTop } = e.target;
        if (leftPaneRef.current && e.target !== leftPaneRef.current) leftPaneRef.current.scrollTop = scrollTop;
        if (rightPaneRef.current && e.target !== rightPaneRef.current) rightPaneRef.current.scrollTop = scrollTop;
    };

    const getStatusColor = () => {
        const s = timesheetStatus?.toLowerCase();
        if (s === 'manager_approved') return 'text-org-blue-primary bg-org-blue-primary/10';
        if (s === 'approved') return 'text-org-green-primary bg-org-green-primary/10';
        if (s === 'rejected') return 'text-red-600 bg-red-50';
        return 'text-gray-600 bg-gray-50';
    };

    if (isTimesheetLoading) return <PageLoader />;

    return (
        <div className="flex flex-col min-h-screen gap-20 font-sans text-text-primary">
            <Breadcrumb title="HR Timesheet Approval" path="Approvals / HR Timesheets / Review" />

            <div className='grid grid-cols-12 gap-20'>
                {/* TOP INFO CARD */}
                <div className='col-span-12 bg-white rounded border border-border shadow-sm p-20'>
                    <div className='flex items-center justify-between mb-20'>
                        <div className='flex items-center gap-10'>
            {/* Back button */}
            <button
                onClick={() => navigate('/approvals/timesheet/hr')}
                className="btn btn-outline-secondary flex items-center gap-5 mb-15"
            >
                <IconChevronLeft size={16} />
                Back
            </button>
                            <div className='flex items-center gap-10 bg-gradient-to-r from-org-blue-primary/10 to-org-green-primary/10 rounded-lg px-20 py-12 border border-org-blue-primary/20'>
                                <IconCalendarMonth size={22} className='text-org-blue-primary' />
                                <div className='text-left'>
                                    <p className='text-[9px] text-org-blue-primary font-semibold uppercase tracking-wide'>Review Period</p>
                                    <p className='text-base font-bold text-gray-800'>{months[tsMonth - 1]} {tsYear}</p>
                                </div>
                            </div>
                        </div>

                        <div className='flex items-center gap-6'>
                            <button
                                onClick={() => setShowRejectModal(true)}
                                className='flex items-center gap-2 bg-red-50 text-red-600 border border-red-200 px-10 py-6 rounded text-xs font-semibold hover:bg-red-100'
                            >
                                <IconBan size={14} />Reject
                            </button>
                            <button
                                onClick={() => setShowApproveModal(true)}
                                className='flex items-center gap-2 bg-org-green-primary text-white px-10 py-6 rounded text-xs font-semibold hover:opacity-90'
                            >
                                <IconCheck size={14} />HR Approve
                            </button>
                        </div>
                    </div>

                    <div className='pt-10 flex items-center justify-between'>
                        <div className='flex items-center gap-40'>
                            <h2 className='text-lg font-bold text-gray-800 pr-20 border-r border-border'>HR Timesheet Review</h2>
                            <div className='flex gap-40 text-xs'>
                                <div>
                                    <p className='text-gray-500 mb-2 uppercase text-[9px] font-bold tracking-wider'>Employee</p>
                                    <p className='font-semibold text-sm whitespace-nowrap'>{employeeName}</p>
                                </div>
                                <div>
                                    <p className='text-gray-500 mb-2 uppercase text-[9px] font-bold tracking-wider'>Status</p>
                                    <span className={`font-bold text-sm uppercase px-8 py-2 rounded ${getStatusColor()}`}>
                                        {timesheetStatus === 'manager_approved' ? 'Manager Approved' : timesheetStatus}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='mt-15 pt-15 border-t border-border flex items-center gap-20 text-xs flex-wrap'>
                        {signedAt && (
                            <div className='flex items-center gap-6 text-org-blue-primary'>
                                <IconSignature size={14} />
                                <span>Signed: {new Date(signedAt).toLocaleString()}</span>
                            </div>
                        )}
                        {managerApprovedAt && (
                            <div className='flex items-center gap-6 text-org-green-primary'>
                                <IconCheck size={14} />
                                <span>Manager Approved: {new Date(managerApprovedAt).toLocaleString()}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* SUMMARY CARDS */}
                <div className="col-span-12 grid grid-cols-3 gap-20">
                    <div className="bg-white p-15 rounded border border-border shadow-sm flex items-center gap-15">
                        <div className="w-40 h-40 rounded bg-org-blue-primary/10 flex items-center justify-center text-org-blue-primary font-bold">
                            <IconClock size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase text-gray-500 font-bold tracking-tight">Total Hours</p>
                            <p className="text-lg font-bold text-org-blue-primary">{calculateGrandTotal().toFixed(1)}</p>
                        </div>
                    </div>
                    <div className="bg-white p-15 rounded border border-border shadow-sm flex items-center gap-15">
                        <div className="w-40 h-40 rounded bg-org-green-primary/10 flex items-center justify-center text-org-green-primary font-bold">
                            <IconBeach size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase text-gray-500 font-bold tracking-tight">Holiday Hours</p>
                            <p className="text-lg font-bold text-org-green-primary">{calculateHolidayHours().toFixed(1)}</p>
                        </div>
                    </div>
                    <div className="bg-white p-15 rounded border border-border shadow-sm flex items-center gap-15">
                        <div className="w-40 h-40 rounded bg-red-100 flex items-center justify-center text-red-600 font-bold">
                            <IconCalendarMonth size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase text-gray-500 font-bold tracking-tight">Leave Hours</p>
                            <p className="text-lg font-bold text-red-600">{calculateLeaveHours().toFixed(1)}</p>
                        </div>
                    </div>
                </div>

                {/* TABLE CARD */}
                <div className='col-span-12 bg-white p-20 rounded border border-border shadow-sm flex flex-col'>
                    <h3 className='text-sm font-semibold text-gray-700 mb-15'>Timesheet Lines</h3>

                    <div className="flex border border-gray-300 rounded overflow-hidden">
                        {/* LEFT SECTION */}
                        <div className="w-[38%] border-r-2 border-gray-400 flex flex-col">
                            <div className="overflow-x-auto overflow-y-auto scrollbar-hide max-h-[450px]" ref={leftPaneRef} onScroll={handleVerticalScroll}>
                                <div className="min-w-[500px] flex flex-col">
                                    <div className="h-[56px] bg-org-blue-primary text-white flex items-center text-[10px] font-bold uppercase sticky top-0 z-[2]">
                                        <div className="w-[40px] px-4 border-r border-white/20 text-center">#</div>
                                        <div className="w-[200px] px-8 border-r border-white/20">Description</div>
                                        <div className="w-[120px] px-8 border-r border-white/20">Code</div>
                                        <div className="flex-grow px-8">Pay Type</div>
                                    </div>
                                    {timeEntries.map((entry, idx) => (
                                        <div key={entry.id} className="h-[48px] border-b border-border flex items-center text-sm">
                                            <div className="w-[40px] px-4 border-r h-full flex items-center justify-center bg-gray-50 text-gray-400 font-bold text-[11px]">{idx + 1}</div>
                                            <div className="w-[200px] px-8 border-r h-full flex items-center">
                                                <span className="text-xs font-medium truncate">{entry.description || entry.project}</span>
                                            </div>
                                            <div className="w-[120px] px-8 border-r h-full flex items-center">
                                                <span className="text-[10px] font-mono bg-gray-100 px-4 py-1 rounded truncate">{entry.projectCode?.slice(0, 8) || '-'}</span>
                                            </div>
                                            <div className="flex-grow px-8 h-full flex items-center">
                                                <span className="text-[10px] font-medium">{entry.payType}</span>
                                            </div>
                                        </div>
                                    ))}
                                    {/* Holiday Row */}
                                    <div className="h-[48px] border-b border-border flex items-center text-sm bg-org-green-primary/5">
                                        <div className="w-[40px] px-4 border-r h-full flex items-center justify-center bg-org-green-primary/10 text-org-green-primary font-bold text-[11px]">
                                            <IconBeach size={14} />
                                        </div>
                                        <div className="w-[200px] px-8 border-r h-full flex items-center">
                                            <span className="text-xs font-medium">Public Holidays</span>
                                        </div>
                                        <div className="w-[120px] px-8 border-r h-full flex items-center">
                                            <span className="text-[10px] text-gray-400">-</span>
                                        </div>
                                        <div className="flex-grow px-8 h-full flex items-center">
                                            <span className="text-[10px] text-gray-400">Time Off</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT SECTION */}
                        <div className="flex-grow flex flex-col overflow-hidden">
                            <div className="overflow-x-auto overflow-y-auto scrollbar-hide max-h-[450px]" ref={rightPaneRef} onScroll={handleVerticalScroll}>
                                <div className="flex">
                                    {periodDays.map((date, dayIdx) => {
                                        const dateStr = formatDateLocal(date);
                                        const holiday = holidayLookup[dateStr];
                                        const leave = leaveLookup[dateStr];
                                        const weekend = isWeekend(date);

                                        const getColumnBg = () => {
                                            if (holiday) return 'bg-org-green-primary/5';
                                            if (leave) return 'bg-red-50';
                                            if (weekend) return 'bg-gray-50';
                                            return '';
                                        };

                                        const getHeaderBgColor = () => {
                                            if (holiday) return '#6CBC50';
                                            if (leave) return '#dc2626';
                                            if (weekend) return '#9ca3af';
                                            return '#012E81';
                                        };

                                        return (
                                            <div key={dayIdx} className={`min-w-[85px] border-r border-border ${getColumnBg()}`}>
                                                <div
                                                    className="h-[56px] text-white flex flex-col items-center justify-center text-[10px] font-medium sticky top-0 z-[2] border-r border-white/20 relative overflow-hidden"
                                                    style={{ backgroundColor: getHeaderBgColor() }}
                                                >
                                                    <span className="font-bold">{date.toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                                                    <span className="opacity-80 text-[9px]">{date.getDate()}</span>
                                                    {(holiday || leave) && !weekend && (
                                                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                                                            <span className="bg-white/30 backdrop-blur-sm px-2 py-0.5 rounded text-[6px] font-bold uppercase tracking-wide whitespace-nowrap">
                                                                {holiday ? 'Holiday' : 'Leave'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {timeEntries.map((entry, entryIdx) => {
                                                    const isLeaveDay = !!leave && !weekend;
                                                    const isProjectLine = !entry.isLeave && !entry.isTimeOff;
                                                    const isFirstProjectLine = entryIdx === 0 && isProjectLine;
                                                    let displayHours = null;
                                                    if (isLeaveDay) {
                                                        if (isFirstProjectLine) displayHours = leave.hours;
                                                    } else {
                                                        displayHours = entry.hours[dayIdx] || null;
                                                    }
                                                    return (
                                                        <div key={entry.id} className={`h-[48px] border-b border-border flex items-center justify-center text-sm ${isLeaveDay && isFirstProjectLine ? 'bg-red-100' : ''}`}>
                                                            <span className={`font-medium ${isLeaveDay && isFirstProjectLine ? 'text-red-600' : 'text-gray-800'}`}>
                                                                {displayHours ? displayHours.toFixed(1) : ''}
                                                            </span>
                                                        </div>
                                                    );
                                                })}

                                                {/* Holiday Row */}
                                                <div className={`h-[48px] border-b border-border flex items-center justify-center text-sm ${holiday ? 'bg-org-green-primary/10' : ''}`}>
                                                    <span className="font-medium text-org-green-primary">
                                                        {holiday ? (8).toFixed(1) : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* TOTAL COLUMN */}
                                    <div className="min-w-[80px] bg-gray-100 sticky right-0 z-[2] border-l-2 border-gray-400">
                                        <div className="h-[56px] bg-org-blue-primary text-white flex items-center justify-center text-[11px] font-bold sticky top-0 z-[2]">
                                            TOTAL
                                        </div>
                                        {timeEntries.map((entry, entryIdx) => {
                                            const projectTotal = calculateRowTotalExcludingLeave(entry.hours);
                                            const leaveHours = entryIdx === 0 ? calculateLeaveHoursForFirstRow() : 0;
                                            return (
                                                <div key={entry.id} className="h-[48px] border-b border-border flex items-center justify-center text-sm font-bold bg-gray-50">
                                                    {(projectTotal + leaveHours).toFixed(1)}
                                                </div>
                                            );
                                        })}
                                        <div className="h-[48px] border-b border-border flex items-center justify-center text-sm font-bold">
                                            {calculateHolidayHours().toFixed(1)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Approve Modal */}
            {showApproveModal && (
                <div className='fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm'>
                    <div className='bg-white p-25 rounded-lg shadow-xl max-w-sm w-full text-center border border-border'>
                        <div className='w-50 h-50 rounded-full bg-org-green-primary/10 flex items-center justify-center mx-auto mb-15'>
                            <IconCheck size={24} className='text-org-green-primary' />
                        </div>
                        <h3 className='font-bold text-gray-800 mb-5 text-lg'>HR Approve Timesheet</h3>
                        <p className='text-gray-500 mb-20 text-sm'>
                            Approve <span className='font-semibold'>{employeeName}'s</span> timesheet for {months[tsMonth - 1]} {tsYear}?
                        </p>
                        <div className='flex gap-10'>
                            <button onClick={() => setShowApproveModal(false)} className='flex-1 py-10 bg-gray-100 text-gray-700 font-semibold text-sm rounded-lg hover:bg-gray-200'>
                                Cancel
                            </button>
                            <button onClick={handleConfirmApprove} className='flex-1 py-10 bg-org-green-primary text-white font-semibold text-sm rounded-lg hover:opacity-90'>
                                HR Approve
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && (
                <div className='fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm'>
                    <div className='bg-white p-25 rounded-lg shadow-xl max-w-md w-full border border-border'>
                        <div className='w-50 h-50 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-15'>
                            <IconBan size={24} className='text-red-600' />
                        </div>
                        <h3 className='font-bold text-gray-800 mb-5 text-lg text-center'>Reject Timesheet</h3>
                        <p className='text-gray-500 mb-15 text-sm text-center'>
                            Please provide a reason for rejecting <span className='font-semibold'>{employeeName}'s</span> timesheet.
                        </p>
                        <textarea
                            className='w-full border border-gray-300 rounded-lg p-12 text-sm mb-20 outline-none focus:border-org-blue-primary resize-none'
                            rows={4}
                            placeholder="Enter rejection reason..."
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                        />
                        <div className='flex gap-10'>
                            <button onClick={() => setShowRejectModal(false)} className='flex-1 py-10 bg-gray-100 text-gray-700 font-semibold text-sm rounded-lg hover:bg-gray-200'>
                                Cancel
                            </button>
                            <button onClick={handleConfirmReject} className='flex-1 py-10 bg-red-600 text-white font-semibold text-sm rounded-lg hover:opacity-90'>
                                Reject
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
